import React from 'react';
import { Button } from 'react-bootstrap';
import PhoneBillsAccountStrip from './PhoneBillsAccountStrip';
import PhoneBillsSegments from './PhoneBillsSegments';
import PhoneBillCardList from './PhoneBillCardList';
import PhoneBillCollapsibleSection from './PhoneBillCollapsibleSection';
import PhoneMobileTracking from './PhoneMobileTracking';
import BillSearch from '../BillSearch';
import { formatShortDateRange } from '../../../utils/dateUtils';
import '../../../css/PhoneBillsView.css';

const PhoneBillsView = ({
  activeTab,
  setActiveTab,
  state,
  showAllBills,
  trackingPeriod,
  setTrackingPeriod,
  setShowUpdateBalance,
  onAddBill,
  onUpdatePaidDate,
  onDelete,
  graphElement,
  formattedStartNextWeek,
  formattedRangeSecondWeek,
  currentWeekStart,
  currentWeekEnd,
  renderOverduePaginationControls,
  renderPaginationControls,
}) => {
  const accountCount = state.bankAccounts?.length || 0;
  const fortnightDue =
    parseFloat(state.totals?.totalThisWeek || 0) + parseFloat(state.totals?.totalNextWeek || 0);

  const summaryText =
    showAllBills && accountCount > 0
      ? `${accountCount} account${accountCount === 1 ? '' : 's'} · $${fortnightDue.toFixed(2)} due this fortnight`
      : null;

  const currentBills = (state.currentWeekBills || []).filter(
    (bill) => !(new Date(bill.due_date) < currentWeekStart && !bill.paid)
  );

  const showFortnightLists =
    showAllBills ||
    !state.bankAccountId ||
    state.currentWeekBills.length > 0 ||
    state.nextWeekBills.length > 0 ||
    (state.overdueBills && state.overdueBills.length > 0);

  const listProps = {
    onUpdatePaidDate,
    bankAccountId: state.bankAccountId,
    onDelete,
    showAllBills,
    bankAccounts: state.bankAccounts,
  };

  return (
    <div className="phone-bills-page">
      <header className="phone-bills-header">
        <PhoneBillsAccountStrip
          bankAccounts={state.bankAccounts}
          currentBankAccountId={state.bankAccountId}
          summaryText={summaryText}
        />
        <PhoneBillsSegments activeTab={activeTab} setActiveTab={setActiveTab} />
      </header>

      <div className="phone-bills-content">
        {activeTab === '#fortnight' && (
          <>
            {!showAllBills && state.bankAccountId && (
              <PhoneMobileTracking
                state={state}
                trackingPeriod={trackingPeriod}
                setTrackingPeriod={setTrackingPeriod}
                setShowUpdateBalance={setShowUpdateBalance}
                graphElement={graphElement}
              />
            )}

            {!showFortnightLists && !showAllBills && (
              <div className="phone-bills-empty">
                <h5>No bills for this account</h5>
                <p>Add a bill to start tracking this account.</p>
                <Button variant="primary" size="sm" onClick={onAddBill}>
                  Add a bill
                </Button>
              </div>
            )}

            {showFortnightLists && (
              <div className="phone-bills-sections">
                {state.overdueBills && state.overdueBills.length > 0 && (
                  <PhoneBillCollapsibleSection
                    title="Overdue"
                    count={state.overdueBills.length}
                    defaultOpen
                    variant="warning"
                  >
                    <PhoneBillCardList bills={state.overdueBills} {...listProps} />
                    {renderOverduePaginationControls && renderOverduePaginationControls()}
                  </PhoneBillCollapsibleSection>
                )}

                <PhoneBillCollapsibleSection
                  title="This week"
                  variant="week"
                  subtitle={
                    currentWeekStart && currentWeekEnd
                      ? `${formatShortDateRange(currentWeekStart, currentWeekEnd)} · Due before ${formattedStartNextWeek}`
                      : formattedStartNextWeek
                        ? `Due before ${formattedStartNextWeek}`
                        : null
                  }
                  count={currentBills.length}
                  defaultOpen
                >
                  <PhoneBillCardList
                    bills={currentBills}
                    {...listProps}
                    emptyMessage="No unpaid bills this week."
                  />
                </PhoneBillCollapsibleSection>

                {state.currentWeekPaidBills?.length > 0 && (
                  <PhoneBillCollapsibleSection
                    title="Paid this week"
                    variant="paid"
                    count={state.currentWeekPaidBills.length}
                    defaultOpen={false}
                  >
                    <PhoneBillCardList bills={state.currentWeekPaidBills} {...listProps} />
                  </PhoneBillCollapsibleSection>
                )}

                <PhoneBillCollapsibleSection
                  title="Next week"
                  variant="next"
                  subtitle={formattedRangeSecondWeek ? `Due within ${formattedRangeSecondWeek}` : null}
                  count={state.nextWeekBills?.length || 0}
                  defaultOpen
                >
                  <PhoneBillCardList
                    bills={state.nextWeekBills}
                    {...listProps}
                    emptyMessage="No bills next week."
                  />
                </PhoneBillCollapsibleSection>

                {state.nextWeekPaidBills?.length > 0 && (
                  <PhoneBillCollapsibleSection
                    title="Next week — already paid"
                    variant="paid"
                    count={state.nextWeekPaidBills.length}
                    defaultOpen={false}
                  >
                    <PhoneBillCardList bills={state.nextWeekPaidBills} {...listProps} />
                  </PhoneBillCollapsibleSection>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === '#future' && (
          <>
            <p className="phone-bills-tab-hint">Bills scheduled after the current fortnight</p>
            {state.filteredBills.length > 0 ? (
              <>
                <PhoneBillCardList bills={state.filteredBills} {...listProps} />
                {renderPaginationControls && (
                  <div className="phone-bills-load-more">{renderPaginationControls()}</div>
                )}
              </>
            ) : (
              <div className="phone-bills-empty">
                <h5>No future bills</h5>
                <p>All scheduled bills are within the current fortnight.</p>
              </div>
            )}
          </>
        )}

        {activeTab === '#recent' && (
          <>
            <p className="phone-bills-tab-hint">Paid in the last month</p>
            {state.filteredBills.length > 0 ? (
              <>
                <PhoneBillCardList bills={state.filteredBills} {...listProps} />
                {renderPaginationControls && (
                  <div className="phone-bills-load-more">{renderPaginationControls()}</div>
                )}
              </>
            ) : (
              <div className="phone-bills-empty">
                <h5>No recently paid bills</h5>
              </div>
            )}
          </>
        )}

        {activeTab === '#search' && (
          <div className="phone-bills-search-wrap">
            <BillSearch
              bankAccountId={state.bankAccountId}
              showAllBills={showAllBills}
              mobileLayout
              onDelete={onDelete}
            />
          </div>
        )}
      </div>

      <button
        type="button"
        className="phone-bills-fab"
        onClick={onAddBill}
        aria-label="Add bill"
        title="Add bill"
      >
        +
      </button>
    </div>
  );
};

export default PhoneBillsView;
